import React, { useState } from 'react';
import { Menu, Plus, Search, X } from 'lucide-react';
import { Button } from '../ui';
import GlobalSearch from './GlobalSearch';

/**
 * v2 top bar — white 56px bar over the content canvas, ported from
 * `Dashboard v2.dc.html`: page title + date, global search (self-contained —
 * see GlobalSearch), a feedback entry (a slot, same reasoning as the bell),
 * notification bell (a slot: the connected shell passes a live
 * `NotificationBell`; nothing renders without one — an inert bell would
 * be a dead control), primary "+ New RFQ".
 *
 * On a phone the same row carries the menu button for the navigation drawer
 * (the sidebar has nowhere to dock), and search steps out of the row: at 375px
 * a permanently open search box and four controls cannot share 56px without
 * every one of them becoming too small to hit. Tapping the magnifier gives the
 * whole bar to the search field and back again — the same field, the same
 * results, just the only thing on screen while it is being used.
 */
export interface AppTopBarProps {
  title: string;
  /** e.g. "Monday, 20 Jul 2026". Omit to hide. */
  date?: string;
  searchPlaceholder?: string;
  /** The notification bell (see `NotificationBell`); omitted = no bell. */
  notificationsSlot?: React.ReactNode;
  /** "Give feedback" (see `FeedbackButton`); omitted = no entry here. */
  feedbackSlot?: React.ReactNode;
  /** Primary action; defaults to "New RFQ". */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Opens the navigation drawer. Passed only on a phone, where the sidebar
   * is not on screen — on a desktop the nav is right there and a second way
   * to reach it would be a button that duplicates a visible panel.
   */
  onMenu?: () => void;
}

export const AppTopBar: React.FC<AppTopBarProps> = ({
  title,
  date,
  searchPlaceholder = 'Search RFQs, suppliers, parts…',
  notificationsSlot,
  feedbackSlot,
  actionLabel = 'New RFQ',
  onAction,
  onMenu,
}) => {
  // Phone only: the search field has taken the whole bar.
  const [searching, setSearching] = useState(false);

  if (searching) {
    return (
      <div className="flex h-top-bar shrink-0 items-center gap-s4 border-b border-line bg-surface px-s5 md:hidden">
        <GlobalSearch placeholder={searchPlaceholder} autoFocus />
        <button
          type="button"
          onClick={() => setSearching(false)}
          aria-label="Close search"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-ink-secondary hover:bg-surface-hover hover:text-ink"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-top-bar shrink-0 items-center gap-[16px] border-b border-line bg-surface px-s7 max-md:gap-s4 max-md:px-s5">
      {onMenu && (
        <button
          type="button"
          onClick={onMenu}
          aria-label="Open navigation"
          className="-ml-s2 flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-ink-secondary hover:bg-surface-hover hover:text-ink md:hidden"
        >
          <Menu className="h-[19px] w-[19px]" />
        </button>
      )}
      <span className="text-[14px] font-semibold text-ink max-md:min-w-0 max-md:truncate">{title}</span>
      {/* The date is orientation, not information — the first thing to go
          when the row is 375px wide. */}
      {date && <span className="hidden whitespace-nowrap text-mono-data text-ink-muted md:inline">{date}</span>}
      <div className="ml-auto flex items-center gap-s4 max-md:shrink-0 max-md:gap-s2">
        <button
          type="button"
          onClick={() => setSearching(true)}
          aria-label="Search"
          className="flex h-9 w-9 items-center justify-center rounded-control text-ink-secondary hover:bg-surface-hover hover:text-ink md:hidden"
        >
          <Search className="h-[18px] w-[18px]" />
        </button>
        <div className="hidden md:block">
          <GlobalSearch placeholder={searchPlaceholder} />
        </div>
        {feedbackSlot}
        {notificationsSlot}
        <Button iconLeft={<Plus />} onClick={onAction} aria-label={actionLabel} className="whitespace-nowrap max-md:h-9 max-md:w-9 max-md:px-0">
          <span className="hidden md:inline">{actionLabel}</span>
        </Button>
      </div>
    </div>
  );
};

export default AppTopBar;
